import React, {Component} from 'react'
import {Link, Redirect} from 'react-router-dom'
import {graphql} from 'react-apollo'
import classnames from 'classnames'
import {get, some, isEmpty, size, includes, flowRight as compose, endsWith} from 'lodash'
import {withNamespaces, Trans} from 'react-i18next'
import {apiErrors} from '@bdswiss/common-enums'
import {LinearProgress} from '@material-ui/core'
import Grid from '@material-ui/core/Grid'
import Hidden from '@material-ui/core/Hidden'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import withStyles from '@material-ui/core/styles/withStyles'
import Header from '../App/Header'
import {config} from '../../config'
import messages from '../../assets/messages'
import {loginRequest, logoutRequest, getConfigVars} from '../../common/utils/requests'
import {CLIENT_DATA_QUERY} from '../../graphql/queries'
import {storeSessInfoInLocalStorage, removeSessInfoFromLocalStorage, getItem, findCompany} from '../../common/utils'
import PageTitle from '../Common/PageTitle'
import {getCookie} from '../../common/utils/browser'
import queryString from 'querystring'
import NotificationBar from '../Common/NotificationBar'
import UiNotification from '../Common/UiNotification'
import AppContext from '../Common/contexts/AppContext'
import Password from '../Common/Password'
import ReCAPTCHA from 'react-google-recaptcha'
import LoadingButton from '../Common/LoadingButton'
import {InnerAppContext, ProvidedProps} from '../../common/types'
import {newStyle} from '../../themes/components'

const styles = newStyle('Login',theme => ({
  mainBody:{
    maxWidth: theme.mainBodyWidth,
    margin:'auto',
    minHeight: '100%',
    position: 'relative' as const,
  },
  wrapper: {
    maxWidth: 450,
    margin: 30
  },
  grid: {
    marginBottom: 40,
  },
  errorGrid: {
    marginBottom: 15,
  },
  error: {
    color: theme.palette.error.main
  },
  icon: {
    marginRight: 8,
    color: '#FF6600'
  },
  formControl: {
    flexBasis: 200,
  },
  signupBtn: {
    padding: 0,
    height: 44,
  },
  highlightedText:{
    fontWeight: 400,
  },
  pointer:{
    cursor: 'pointer',
    textDecoration: 'underline'
  },
  title: {
    paddingLeft: 0
  },
}))

const recaptchaRef = React.createRef<ReCAPTCHA>()
type LoginProps = ProvidedProps<ReturnType<typeof styles>> & {
  redirectUrl: Location
  viewer: any
  loading: boolean
}
class Login extends Component<LoginProps,any> {
  static contextType = AppContext
  context!: InnerAppContext
  constructor(props) {
    super(props)
    this.state = {
      form: {
        email: '',
        password: '',
      },
      showPassword: false,
      loginError: '',
      errors: {},
      showEmbeddedUi: false,
      embedded: sessionStorage.getItem('embedded') ?? undefined,
      submitLoading: false,
    }
  }

  componentDidMount() {
    const {location} = this.props
    const queryParams = queryString.parse(location.search.replace('?', ''))
    if (get(queryParams, 'error')) {
      const message = get(apiErrors, queryParams.error as any)
      this.setState<'loginError'>({loginError: message.localization.t!(getItem('locale') || 'en')})
    }
    if (get(queryParams, 'embedded'))
      window.sessionStorage && sessionStorage.setItem('embedded', 'true')

    if (get(queryParams, 'webinarid'))
      window.sessionStorage && sessionStorage.setItem('webinarid', queryParams.webinarid as string)

    if (get(queryParams, 'email')) {
      const email: string = get(queryParams, 'email') as string
      this.setState(state => ({form: {...state.form, email: email}}))
    }

    getConfigVars()
      .then((res) => {
        this.setState<'isRecaptchaLoginEnabled'>({isRecaptchaLoginEnabled: res.recaptchaLoginEnabled})
      })
      .catch((err) => {
        this.setState<'isRecaptchaLoginEnabled'>({isRecaptchaLoginEnabled: false})
      })
  }

  handleChange(prop, value) {
    this.setState(state => ({
      form: {
        ...state.form,
        [prop]: value,
      },
      loginError: '',
      errors: {
        ...state.errors,
        [prop]: !value,
      },
      submitLoading: false,
    }))
  }

  handleClickShowPassword() {
    this.setState<'showPassword'>(state => ({
      showPassword: !state.showPassword
    }))
  }

  storeSession(relatedEntities, company) {
    const sessionInfo = {
      relatedEntities,
      company,
    }
    storeSessInfoInLocalStorage(sessionInfo)
  }

  async onLogin(redirectTo) {
    const {form, form: {email, password}, embedded, isRecaptchaLoginEnabled} = this.state
    const {location} = this.props
    const {common: {autoTestEmail}} = config
    const company = findCompany()
    const queryParams = queryString.parse(location.search.replace('?', ''))
    const webinarid = get(queryParams,'webinarid') || sessionStorage.getItem('webinarid') || ''

    let recaptcha
    if (isRecaptchaLoginEnabled) {
      if (!endsWith(this.state.form.email, autoTestEmail)) {
        if (config.common.reCaptcha.isInvisible) {
          recaptcha = await recaptchaRef.current.executeAsync()
        } else {
          recaptcha = recaptchaRef.current.getValue()
        }
      }
    }

    const errors = {}
    for (const field of Object.keys(form)) {
      errors[field] = isEmpty(form[field])
    }

    if (some(errors)) {
      this.setState<'errors'>({errors})
      return
    }

    this.setState<'submitLoading'>({submitLoading: true})
    loginRequest(email, password, recaptcha).then((res) => {
      const {relatedEntities} = res
      if (res.lastCountryWarning && relatedEntities.length === 1 && !res.wtRedirect) {
        this.props.history.push('/login-verification')
      } else {
        if (embedded) {
          this.setState<'showEmbeddedUi'>({showEmbeddedUi: true})
        } else if (!isEmpty(webinarid)) {
          window.location.href = relatedEntities[0].frontendUrl + '/webinar?webinarid='+ webinarid +'&locationTrack=login'
        } else {
          if (relatedEntities.length > 1) {
            this.setState<'relatedEntities'>({relatedEntities})
            this.props.history.push('/choose-entity', {entities:relatedEntities, redirectTo: redirectTo})
          } else {
            const sameCompany = relatedEntities[0].entity === get(company, 'value')
            sameCompany && this.storeSession(relatedEntities, relatedEntities[0].entity)

            if (sameCompany &&  this.isEligibleForBDSMigration()) {
              this.props.history.push('/migrate', {redirectTo: redirectTo})
            } else {
              if (window.location.host !== relatedEntities[0].frontendUrl && includes(relatedEntities[0].frontendUrl, 'client-autologin')) {
                window.location = relatedEntities[0].frontendUrl
              } else {
                this.props.history.push(/logout/i.test(redirectTo) ? '/'
                  : redirectTo, {prevPath: this.props.location.pathname, redirectTo: redirectTo})
              }
            }
          }
        }
      }
    }).catch((e) => {
      const {classes} = this.props
      const {weblinks: {contact}} = config
      const elementError = get(messages, e) && <Trans {...messages[e]}
        components={[<a href={contact} target="_blank"  rel="noopener noreferrer" className={classnames(classes.pointer, classes.error)}>support</a>]} />
      const message = (elementError) ? elementError
        : (e instanceof Error ?
          (get(messages, e.message) ? <Trans {...messages[e.message]} className={classnames(classes.error)} /> :  e.message)
          : e)
      this.setState({loginError: message, submitLoading: false})
    })
  }

  isEligibleForBDSMigration() {
    const {relatedEntities} = this.state
    return size(relatedEntities) < 2 && config.featuresConfig.migrateCompany.enable
      && !getCookie('__mig-rejected') && !!getCookie('RegulationAccepted')
  }

  closeEmbeddedIframe() {
    sessionStorage.removeItem('embedded')
    const {weblinks:{websiteUrl}} = config
    window.parent.postMessage('success', websiteUrl)
  }

  render() {
    const {classes, t, viewer, loading, redirectUrl, location: {state: locationState}} = this.props
    const {errors, loginError, showPassword, apolloClient, showEmbeddedUi, isRecaptchaLoginEnabled, submitLoading, form: {email, password}} = this.state
    const {common:{reCaptcha:{siteKey, isInvisible}, autoTestEmail}, key, weblinks:{websiteUrl}, disabledRegistration} = config

    const viewerType = get(viewer, '__typename')
    const fromUrl = get(locationState, 'from.pathname', '/')
    const fromUrlParams = get(locationState, 'from.search', '')
    const redirectAfterLogin = !includes(fromUrl, 'logout') ? `${fromUrl}${fromUrlParams}` : '/'
    const urlParam = this.props.location.search.replace('?', '')
    // const trackingEventsUrl = queryString.parse(this.props.location.search.replace('?', ''))
    // const eventUrlKey = get(trackingEventsUrl,'subcampid','')
    // const eventID = !eventUrlKey.includes('LTCOMP') && eventUrlKey //exclude Live Trading Competition Events
    // const eventCountry = eventID && get(get(countries, eventID.substring(0,2).toLowerCase()),'label')
    // const eventDate = eventID && moment(eventID.substring(2), 'YYYYMMDD').isValid() && moment(eventID.substring(2), 'YYYYMMDD').format('DD/MM/YYYY')
    const eventID = null
    const showEventNotification = false //eventID && eventCountry && eventDate
    const webinarid = get(queryString.parse(urlParam), 'webinarid')
    const redirectToWebsiteUrl = key === 'bdSwissMauritius' && websiteUrl

    const automationEmail = endsWith(this.state.form.email, autoTestEmail)
    //@ts-ignore
    const {themePreference} = this.context

    if (redirectUrl) {
      window.location = redirectUrl
      return null
    }

    if (viewer && viewerType === 'Client') {
      if (eventID) {
        logoutRequest()
          .then(() => {
            removeSessInfoFromLocalStorage()
            apolloClient && apolloClient.clearStore()
          })
      } else {
        return <Redirect to='/' />
      }
    }

    if (loading) {
      document.body.classList.add('loadingApp')
      return <LinearProgress />
    } else {
      document.body.classList.remove('loadingApp')
    }
    return (
      <div className={classes.mainBody}>
        <Header redirectToWebsiteUrl={redirectToWebsiteUrl} outer/>
        <Grid container>
          {/* @ts-ignore*/}
          <Hidden xsDown>
            <Grid item sm={3} />
          </Hidden>
          <Grid item sm={9} xs={12} className={classes.wrapper}>
            <Grid container spacing={1} className={!showEventNotification ? classes.grid : ''}>
              <PageTitle modal customClasses={{title: classes.title}}>
                <Trans {...messages.login} />
              </PageTitle>
            </Grid>
            {/*{showEventNotification && <Grid container spacing={1} className={classes.grid}>*/}
            {/*  <NotificationBar status='info'>*/}
            {/*    <Trans {...messages.registerForEventLogin} values={{eventCountry: eventCountry, eventDate:eventDate}}*/}
            {/*      components={[<span className={classes.highlightedText}>registration</span>]}/>*/}
            {/*  </NotificationBar>*/}
            {/*</Grid>}*/}
            {get(locationState, 'resetPassword') && <Grid container spacing={1} className={classes.grid}>
              <NotificationBar status='success'>
                <Trans {...messages.resetPasswordNotification} />
              </NotificationBar>
            </Grid>}
            <Grid container alignItems="stretch" direction="column" spacing={4} className={classes.grid}>
              <Grid item>
                <TextField
                  id="email"
                  label={t(messages.email.i18nKey, messages.email.defaults)}
                  type="email"
                  value={email}
                  fullWidth
                  autoFocus
                  onChange={(e) => this.handleChange('email', e.target.value)}
                  error={get(errors, 'email', false)}
                  helperText={get(errors, 'email', false) && t(messages.requiredField.i18nKey, messages.requiredField.defaults)}
                />
              </Grid>
              <Grid item>
                <Password
                  classes={classes}
                  showPassword={showPassword}
                  onChange={(e) => this.handleChange('password', e.target.value)}
                  onKeyPress={(e) => e.charCode === 13 && this.onLogin(redirectAfterLogin)}
                  error={get(errors, 'password', false)}
                  onClickShow={() => this.handleClickShowPassword()}
                  errorText={<Trans {...messages.requiredField} />}
                  value={password}
                />
              </Grid>
              <Grid item>
                <Grid container justifyContent={'flex-end'}>
                  <Grid item>
                    <Link to={(urlParam) ? `/forgot-password?${urlParam}` : '/forgot-password'}>
                      <Typography variant='body2' color="primary">
                        <Trans {...messages.forgotPassword} />
                      </Typography>
                    </Link>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            {loginError && <Grid container justifyContent="center" className={classes.errorGrid}>
              <Typography variant='body1' className={classes.error}>
                {loginError}
              </Typography>
            </Grid>}
            <Grid container spacing={4} alignItems="center">
              <Grid item sm={6} xs={12}>
                <LoadingButton
                  variant="contained"
                  size="large"
                  fullWidth
                  onKeyPress={(e) => e.charCode === 13 && this.onLogin(redirectAfterLogin)}
                  onClick={() => this.onLogin(redirectAfterLogin)}
                  disabled={isEmpty(errors) || some(errors) || submitLoading}
                  noMargin
                  hideProgressBar={!submitLoading || some(errors)}
                >
                  <Trans {...messages.loginButton} />
                </LoadingButton>
              </Grid>
              {!disabledRegistration && <Grid item sm={6} xs={12}>
                <Link to={(urlParam) ? (!isEmpty(webinarid)) ? `/register?${urlParam}` : `/register/${urlParam}` : '/register'}>
                  <Typography variant='body2' color="primary">
                    <Trans {...messages.signUp} />
                  </Typography>
                </Link>
              </Grid>}
            </Grid>
          </Grid>
          {isRecaptchaLoginEnabled && !automationEmail && <Grid item xs={12}>
            <ReCAPTCHA
              sitekey={siteKey}
              ref={recaptchaRef}
              theme={themePreference}
              size={isInvisible ? 'invisible' : 'normal'}
            />
          </Grid>}
        </Grid>
        {showEmbeddedUi && <UiNotification
          open={true}
          status={'success'}
          type="document-upload"
          buttonMessage={t(messages.close.i18nKey)}
          onClose={() => this.closeEmbeddedIframe()}
        />}
      </div>
    )
  }
}

export default compose(
  withNamespaces(),
  withStyles(styles, {withTheme: true}),
  graphql(CLIENT_DATA_QUERY, {
    props: (props) => ({
      loading: props.data?.loading,
      redirectUrl: get(props.data?.error, 'networkError.result.data.redirectUrl'),
      viewer: get(props.data, 'viewer')
    }),
    skip: (props) => {
      const isError = get(props, 'location.state.isError', false)
      const loggedOut = get(props, 'location.state.loggedOut', false)
      return isError || loggedOut
    }
  })
)(Login)
